import { createApiRef } from '@backstage/core-plugin-api';
import { SonarQubeGroup } from "./groups/types";
import {
  CreateProjectRequest,
  SonarQubeProject,
  SonarQubeProjectBinding,
  SonarQubeProjectDescription,
  SonarQubeProjectQualityGate,
  SonarQubeProjectStatus,
  UpdateProjectRequest,
} from './projects/types';
import {
  CreateServiceGroupRequest,
  ServiceGroupRole,
  SonarQubeServiceGroup,
  SonarQubeServiceGroupMembership,
  UpdateServiceGroupRequest,
} from './service-groups/types';
import { SonarQubeQualityGate } from './quality-gates/types';

export interface SonarQubeApi {
  /** Caller must be an admin of the product team — enforced by the backend. */
  createServiceGroup(
    request: CreateServiceGroupRequest,
  ): Promise<{ id: string }>;
  /** Caller must be a service group member (403 otherwise). `undefined` on 404. */
  getServiceGroup(
    serviceGroupId: string,
  ): Promise<SonarQubeServiceGroup | undefined>;
  /** The caller's own membership. `undefined` on 404 (not a member). */
  getMembership(
    serviceGroupId: string,
  ): Promise<SonarQubeServiceGroupMembership | undefined>;
  /** Caller must be a service group admin — enforced by the backend. */
  updateServiceGroup(
    serviceGroupId: string,
    request: UpdateServiceGroupRequest,
  ): Promise<void>;
  /** Caller must be a service group member with `maintain` on the repo — enforced by the backend. */
  createProject(
    serviceGroupId: string,
    request: CreateProjectRequest,
  ): Promise<void>;
  /**
   * Caller must be a service group member with `maintain` on the repo — enforced by the backend. Also runs the
   * repo's analysis workflow, which applies the description.
   */
  updateProject(
    serviceGroupId: string,
    projectKey: string,
    request: UpdateProjectRequest,
  ): Promise<void>;
  /** Caller must be a service group member — enforced by the backend. */
  getProjects(serviceGroupId: string): Promise<SonarQubeProject[]>;
  /** Latest quality gate result per project. Caller must be a service group member. */
  getProjectStatuses(serviceGroupId: string): Promise<SonarQubeProjectStatus[]>;
  /** Repository binding per project. Caller must be a service group member. */
  getProjectBindings(
    serviceGroupId: string,
  ): Promise<SonarQubeProjectBinding[]>;
  /** Description per project. Caller must be a service group member. */
  getProjectDescriptions(
    serviceGroupId: string,
  ): Promise<SonarQubeProjectDescription[]>;
  /** Quality gate per project. Caller must be a service group member. */
  getProjectQualityGates(
    serviceGroupId: string,
  ): Promise<SonarQubeProjectQualityGate[]>;
  /** SonarQube groups with their users. Caller must be a service group member. */
  getGroups(serviceGroupId: string): Promise<SonarQubeGroup[]>;
  /** Caller must be a service group admin — enforced by the backend. `entraId` is the target's Entra id. */
  addUserToGroup(
    serviceGroupId: string,
    role: ServiceGroupRole,
    entraId: string,
  ): Promise<void>;
  /** Caller must be a service group admin — enforced by the backend. `entraId` is the target's Entra id. */
  removeUserFromServiceGroup(
    serviceGroupId: string,
    entraId: string,
  ): Promise<void>;
  /** Caller must be a service group admin — enforced by the backend. */
  deleteServiceGroup(serviceGroupId: string): Promise<void>;
  /** Quality gates assignable to new projects. Caller must be a member of any service group. */
  listQualityGates(): Promise<SonarQubeQualityGate[]>;
}

export const sonarqubeApiRef = createApiRef<SonarQubeApi>({
  id: 'plugin.sonarqube.api',
});
